import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import AuthLayout from '../../components/AuthLayout';
import { ForgotPasswordState, DEFAULT_STATE, VALIDATION_RULES, SUBMITTED_MESSAGE } from './config';

const ForgotPassword: React.FC = () => {
  const [state, setState] = useState<ForgotPasswordState>(DEFAULT_STATE);
  const supabase = useSupabaseClient();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.email.trim()) {
      setState(prev => ({ ...prev, error: VALIDATION_RULES.EMAIL.REQUIRED }));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())) {
      setState(prev => ({ ...prev, error: VALIDATION_RULES.EMAIL.INVALID }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Errors are logged but deliberately not surfaced: the confirmation
      // message must be the same whether or not the email has an account
      // (no user enumeration).
      const { error } = await supabase.auth.resetPasswordForEmail(state.email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) {
        console.error('Password reset request error:', error);
      }
    } catch (err) {
      console.error('Password reset request error:', err);
    } finally {
      setState(prev => ({ ...prev, loading: false, submitted: true }));
    }
  };

  return (
    <AuthLayout>
      {state.submitted ? (
        <div className="auth-card__status">
          <div className="auth-card__status-icon">✓</div>
          <p>{SUBMITTED_MESSAGE}</p>
        </div>
      ) : (
        <>
          <h1 className="auth-card__title">Reset password</h1>
          <p className="auth-card__subtitle">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={state.email}
                onChange={e => setState(prev => ({ ...prev, email: e.target.value, error: null }))}
                placeholder="you@terpkos.com"
                disabled={state.loading}
                autoComplete="email"
              />
            </div>

            {state.error && (
              <div className="error-message">
                {state.error}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={state.loading}
            >
              {state.loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
        </>
      )}

      <div className="auth-card__footer">
        <button
          type="button"
          className="link-button"
          onClick={() => navigate('/login')}
          disabled={state.loading}
        >
          Back to sign in
        </button>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
